export class ApiError extends Error {
    details;
    constructor(message, details) {
        super(message);
        this.name = 'ApiError';
        this.details = details;
    }
}
/** HTTP status behind a thrown error, when it came from an API call. */
export const apiErrorStatus = (error) => {
    const status = error?.details?.status;
    return typeof status === 'number' ? status : undefined;
};
/**
 * Would trying this again plausibly work?
 *
 * A 403 will fail identically forever and a 404 will not appear on retry;
 * retrying either wastes the user's time and hides the real answer. A 429, a
 * timeout, or a server error is a moment in time. An error with no status at
 * all is a network/transport failure, which is the most retryable kind.
 */
export const isRetryableApiError = (error) => {
    const status = apiErrorStatus(error);
    if (status === undefined)
        return true;
    if (status === 408 || status === 425 || status === 429)
        return true;
    return status >= 500;
};
/** A refusal the user cannot fix by waiting — they lack access, or it is gone. */
export const isAccessApiError = (error) => {
    const status = apiErrorStatus(error);
    return status === 401 || status === 403;
};
/** Code the entitlement gate answers with when a plugin is off for the company. */
const PLUGIN_NOT_ENABLED = 'PLUGIN_NOT_ENABLED';
export const accessDenialReason = (error) => {
    if (!isAccessApiError(error))
        return null;
    const apiError = error;
    if (apiError.message === PLUGIN_NOT_ENABLED)
        return { kind: 'module' };
    const permission = apiError.details?.missingPermission;
    if (typeof permission === 'string' && permission)
        return { kind: 'permission', permission };
    return { kind: 'unknown' };
};
/**
 * Turn a thrown request failure into something worth reading.
 *
 * Error screens showed the raw thrown message — "HTTP 429", "Too many
 * requests from this IP, please try again later" — as a dead end, with no
 * statement of what it means and no way forward but reloading by hand (QA
 * ledger LC-012). Every plugin has the same screen and the same gap, so the
 * classification lives here rather than in any one of them.
 *
 * The raw message is deliberately NOT the headline: it is written for whoever
 * wrote the server, and for the cases that matter most (rate limiting, access,
 * a server fault) it says less than the status already does.
 */
export const describeApiFailure = (error) => {
    const status = apiErrorStatus(error);
    if (status === 429) {
        return {
            title: 'Too many requests in a short time',
            detail: 'This page is being rate limited. It clears on its own — wait a moment and try again.',
            retryable: true,
        };
    }
    const denial = accessDenialReason(error);
    if (denial?.kind === 'module') {
        return {
            title: 'This module is not enabled for you here',
            detail: 'This company has not given you this module. Ask a company administrator to assign you a role in this company.',
            retryable: false,
        };
    }
    if (denial) {
        return {
            title: 'Not available to your account',
            detail: denial.kind === 'permission'
                ? `Your role does not include the permission "${denial.permission}". Ask a company administrator for a role that has it.`
                : 'Your account does not have access to this data. Ask an administrator if you expected to see it.',
            retryable: false,
        };
    }
    if (status === 404) {
        return {
            title: 'Not found',
            detail: 'This record does not exist, or it has been removed.',
            retryable: false,
        };
    }
    if (typeof status === 'number' && status >= 500) {
        return {
            title: 'The server could not answer',
            detail: 'Something went wrong on our side. Try again — if it keeps happening, report it.',
            retryable: true,
        };
    }
    if (status === undefined) {
        return {
            title: 'Could not reach the server',
            detail: 'The request did not complete. Check your connection and try again.',
            retryable: true,
        };
    }
    return {
        title: 'The request did not complete',
        detail: error instanceof Error && error.message ? error.message : `The server returned ${status}.`,
        retryable: isRetryableApiError(error),
    };
};
/**
 * A polling interval that stops once the query is failing.
 *
 * `refetchInterval: 2_000` keeps firing on the interval no matter what the
 * query's last result was — including while it is failing. Under a rate limit
 * that is the worst possible behaviour: the poll spends the very budget that
 * is already exhausted, so a handful of open pages keep an IP locked out long
 * after the person stopped clicking, and the lockout outlives its own cause
 * (QA ledger INV2-003).
 *
 * Polling resumes by itself as soon as a request succeeds again, so the only
 * thing this gives up is polling through an outage — which was never doing
 * anything except making it worse. Every screen that polls now offers an
 * explicit retry (QA ledger XC2-001), so there is a way back that does not
 * depend on the poll.
 *
 * Usage: `refetchInterval: pollUnlessFailing(2_000)`.
 */
export const pollUnlessFailing = (intervalMs) => (query) => query.state.status === 'error' ? false : intervalMs;
